import { useState, useMemo } from 'react';
import { useLang } from '../../config/i18n';
import { MONTHS } from '../../config/constants';
import { Modal, SaveBtn } from '../shared/UI';

export default function OpsManagerTab({ monthly, units, opsConfig, setOpsConfig, leases }){
  const {S, lang, dir, font} = useLang();
  const T = S.ops;
  const [editTiers,  setEditTiers]  = useState(false);
  const [tiersForm,  setTiersForm]  = useState(null);
  const [editMonth,  setEditMonth]  = useState(null);
  const [monthForm,  setMonthForm]  = useState({});

  const DEFAULT_OPS = { managerName:"حامد", fixedSalary:1500, tiers:[{label:"خسارة",minNet:-999999999,maxNet:0,pct:0},{label:"مستوى ١",minNet:0,maxNet:15000,pct:10},{label:"مستوى ٢",minNet:15000,maxNet:30000,pct:13},{label:"مستوى ٣",minNet:30000,maxNet:999999999,pct:15}], monthlyData:{} };
  const cfg = { ...DEFAULT_OPS, ...(opsConfig||{}), tiers:(Array.isArray(opsConfig?.tiers)&&opsConfig.tiers.length>0?opsConfig.tiers:DEFAULT_OPS.tiers), monthlyData:(opsConfig?.monthlyData||{}) };

  function calcBonus(netIncome, tiers){
    if(netIncome<=0) return 0;
    const tier = [...tiers].reverse().find(t=>netIncome>t.minNet);
    return tier ? Math.round(netIncome * tier.pct / 100) : 0;
  }

  function calcTotal(netIncome, tiers, fixed){ return fixed + calcBonus(netIncome,tiers); }

  function getTierLabel(netIncome, tiers){
    if(netIncome<=0) return lang==="en"?"No commission":"لا عمولة";
    const tier = [...tiers].reverse().find(t=>netIncome>t.minNet);
    return tier ? `${tier.pct}% (${tier.label})` : "—";
  }

  // Build monthly data — merge with recorded monthly ops
  const monthlyRows = useMemo(()=>{
    return MONTHS.map(m=>{
      const totalRev  = units.reduce((s,u)=>s+Number((monthly[`${u.id}-${m.key}`]||{}).rentCollected||0),0);
      const totalMaint= units.reduce((s,u)=>s+Number((monthly[`${u.id}-${m.key}`]||{}).maintenanceCost||0),0);
      const leaseCost = units.reduce((s,u)=>{ const l=(leases||{})[u.id]||{}; return s+Math.round((Number(l.annualCost)||0)/12); },0);
      const netIncome = totalRev - totalMaint - leaseCost;
      const override  = cfg.monthlyData?.[m.key];
      const net       = override?.netIncome!==undefined ? override.netIncome : netIncome;
      const bonus     = calcBonus(net, cfg.tiers);
      const total     = calcTotal(net, cfg.tiers, cfg.fixedSalary);
      return { ...m, totalRev, totalMaint, leaseCost, netIncome, net, bonus, total, note:override?.note||"", hasOverride:!!override };
    });
  },[monthly,units,cfg]);

  const ytdTotal   = monthlyRows.filter(r=>r.net>0).reduce((s,r)=>s+r.total,0);
  const ytdBonus   = monthlyRows.filter(r=>r.net>0).reduce((s,r)=>s+r.bonus,0);
  const maxBar     = Math.max(...monthlyRows.map(r=>Math.max(r.total,1)),1);

  const pc = v=>v>0?"#22c55e":v<0?"#ef4444":"#64748b";

  return(
    <div style={{padding:"22px 20px",maxWidth:1100,margin:"0 auto"}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:22,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:800,color:"#c8a03c",margin:0}}>👨‍💼 أداء مدير العمليات — {cfg.managerName}</h2>
          <p style={{fontSize:13,color:"#475569",margin:"4px 0 0"}}>{T.subtitle}</p>
        </div>
        <button onClick={()=>{ setTiersForm(JSON.parse(JSON.stringify(cfg))); setEditTiers(true); }}
          style={{padding:"8px 16px",borderRadius:10,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.25)",color:"#c8a03c",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
          ⚙️ تعديل الهيكل
        </button>
      </div>

      {/* KPI row */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {[
          {l:T.fixedSalary,           v:cfg.fixedSalary.toLocaleString()+" "+(lang==="en"?"SAR/mo":"ريال/شهر"), c:"#e2e8f0"},
          {l:T.ytdBonus,              v:ytdBonus.toLocaleString()+" "+(lang==="en"?"SAR":"ريال"),             c:"#22c55e"},
          {l:T.ytdTotal,              v:ytdTotal.toLocaleString()+" "+(lang==="en"?"SAR":"ريال"),             c:"#c8a03c"},
        ].map((k,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"14px 18px",flex:1,minWidth:150,textAlign:"right"}}>
            <div style={{fontSize:11,color:"#64748b",marginBottom:4}}>{k.l}</div>
            <div style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div>
          </div>
        ))}

        {/* Tier summary */}
        <div style={{background:"rgba(167,139,250,0.06)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:12,padding:"14px 18px",flex:2,minWidth:240}}>
          <div style={{fontSize:11,color:"#64748b",marginBottom:8}}>{T.commStruct}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {cfg.tiers.map((t,i)=>(
              <span key={i} style={{fontSize:11,background:"rgba(167,139,250,0.1)",color:"#a78bfa",padding:"3px 10px",borderRadius:6,fontWeight:700}}>
                {t.label}: {t.pct}%
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"16px 18px",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:14}}>إجمالي راتب {cfg.managerName} شهرياً (ثابت + عمولة)</div>
        <div style={{display:"flex",gap:6,alignItems:"flex-end",overflowX:"auto",paddingBottom:4}}>
          {monthlyRows.map(r=>{
            const h = Math.round((r.total/maxBar)*100);
            const bh= Math.round((r.bonus/maxBar)*100);
            return(
              <div key={r.key} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,flex:1,minWidth:52,cursor:"pointer"}} onClick={()=>{ setMonthForm({netIncome:String(r.hasOverride?r.net:r.netIncome),note:r.note}); setEditMonth(r.key); }}>
                <div style={{display:"flex",gap:1,alignItems:"flex-end",height:100}}>
                  <div style={{width:16,height:Math.max(h-bh,2),borderRadius:"3px 3px 0 0",background:"rgba(200,160,60,0.5)"}}/>
                  <div style={{width:16,height:Math.max(bh,bh>0?2:0),borderRadius:"3px 3px 0 0",background:"rgba(34,197,94,0.75)"}}/>
                </div>
                <div style={{fontSize:9,color:"#475569"}}>{r.short}</div>
                <div style={{fontSize:9,fontWeight:700,color:"#c8a03c"}}>{r.total>0?r.total.toLocaleString():""}</div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:14,marginTop:8}}>
          {[["rgba(200,160,60,0.5)",T.fixed],["rgba(34,197,94,0.75)",T.commission]].map(([c,l])=>(
            <span key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#64748b"}}>
              <span style={{width:10,height:10,borderRadius:2,background:c,display:"inline-block"}}/>
              {l}
            </span>
          ))}
          <span style={{fontSize:11,color:"#475569"}}>— انقر على أي شهر لتعديل صافي الدخل</span>
        </div>
      </div>

      {/* Monthly table */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,overflow:"hidden",overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:780}}>
          <thead>
            <tr style={{background:"rgba(0,0,0,0.3)"}}>
              {[T.colMonth,T.colRev,T.colLease,T.colMaint,T.colNet,T.colPct,T.colBonus,T.colTotal,T.colNotes,""].map(h=>(
                <th key={h} style={{padding:"10px 12px",textAlign:"right",fontSize:10,color:"#475569",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyRows.map(r=>(
              <tr key={r.key} style={{borderTop:"1px solid rgba(255,255,255,0.04)",background:r.hasOverride?"rgba(167,139,250,0.03)":"transparent"}}>
                <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"#e2e8f0",whiteSpace:"nowrap"}}>
                  {r.label.replace(" ٢٠٢٦","")}
                  {r.hasOverride&&<span style={{fontSize:9,color:"#a78bfa",marginRight:4}}>✎</span>}
                </td>
                <td style={{padding:"10px 12px",fontSize:12,color:"#22c55e"}}>{r.totalRev>0?r.totalRev.toLocaleString():"—"}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"#ef4444"}}>{r.leaseCost>0?"-"+r.leaseCost.toLocaleString():"—"}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"#f97316"}}>{r.totalMaint>0?"-"+r.totalMaint.toLocaleString():"—"}</td>
                <td style={{padding:"10px 12px"}}>
                  <span style={{fontSize:13,fontWeight:700,color:pc(r.net)}}>{r.net!==0?(r.net>0?"+":"")+r.net.toLocaleString():"—"}</span>
                </td>
                <td style={{padding:"10px 12px",fontSize:12,color:"#a78bfa"}}>{getTierLabel(r.net,cfg.tiers)}</td>
                <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:r.bonus>0?"#22c55e":"#334155"}}>
                  {r.bonus>0?r.bonus.toLocaleString()+" ريال":"—"}
                </td>
                <td style={{padding:"10px 12px"}}>
                  <span style={{fontSize:14,fontWeight:800,color:"#c8a03c"}}>{r.total.toLocaleString()} ريال</span>
                </td>
                <td style={{padding:"10px 12px",fontSize:11,color:"#475569"}}>{r.note||"—"}</td>
                <td style={{padding:"10px 12px"}}>
                  <button onClick={()=>{ setMonthForm({netIncome:String(r.hasOverride?r.net:r.netIncome),note:r.note}); setEditMonth(r.key); }}
                    style={{padding:"3px 8px",borderRadius:6,background:"rgba(200,160,60,0.08)",border:"1px solid rgba(200,160,60,0.18)",color:"#c8a03c",fontSize:10,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    تعديل
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Tiers Modal */}
      {editTiers&&tiersForm&&(
        <Modal title={`⚙️ تعديل هيكل عمولة ${tiersForm.managerName}`} onClose={()=>setEditTiers(false)} wide>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>اسم المدير</label>
              <input value={tiersForm.managerName} onChange={e=>setTiersForm(f=>({...f,managerName:e.target.value}))}
                style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,outline:"none",fontFamily:"'Tajawal',sans-serif",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>الراتب الثابت (ريال/شهر)</label>
              <input type="number" value={tiersForm.fixedSalary} onChange={e=>setTiersForm(f=>({...f,fixedSalary:Number(e.target.value)||0}))}
                style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,outline:"none",direction:"ltr",textAlign:"left",boxSizing:"border-box"}}/>
            </div>
          </div>

          <div style={{fontSize:12,color:"#94a3b8",marginBottom:10,fontWeight:700}}>مستويات العمولة</div>
          {tiersForm.tiers.map((t,i)=>(
            <div key={i} style={{display:"flex",gap:10,alignItems:"center",marginBottom:8,background:"rgba(255,255,255,0.03)",padding:"10px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.06)"}}>
              <input value={t.label} onChange={e=>setTiersForm(f=>({...f,tiers:f.tiers.map((x,j)=>j===i?{...x,label:e.target.value}:x)}))}
                style={{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:7,padding:"7px 10px",color:"#e2e8f0",fontSize:12,outline:"none",fontFamily:"'Tajawal',sans-serif",direction:"rtl"}}
                placeholder="اسم المستوى"/>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:12,color:"#64748b"}}>نسبة</span>
                <input type="number" value={t.pct} onChange={e=>setTiersForm(f=>({...f,tiers:f.tiers.map((x,j)=>j===i?{...x,pct:Number(e.target.value)||0}:x)}))}
                  style={{width:60,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:7,padding:"7px 8px",color:"#a78bfa",fontSize:14,fontWeight:700,outline:"none",textAlign:"center"}}/>
                <span style={{color:"#64748b"}}>%</span>
              </div>
              <div style={{display:"flex",gap:4,alignItems:"center",fontSize:11,color:"#475569"}}>
                <input type="number" value={t.maxNet===Infinity?999999:t.maxNet} onChange={e=>{ const v=Number(e.target.value); setTiersForm(f=>({...f,tiers:f.tiers.map((x,j)=>j===i?{...x,maxNet:v>=999999?Infinity:v}:x).map((x,j)=>j===i+1?{...x,minNet:v>=999999?Infinity:v}:x)})); }}
                  style={{width:80,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:7,padding:"5px 6px",color:"#e2e8f0",fontSize:11,outline:"none",direction:"ltr"}}
                  placeholder="حد أعلى"/>
                <span>ريال</span>
              </div>
            </div>
          ))}

          {/* Preview */}
          <div style={{background:"rgba(167,139,250,0.07)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
            <div style={{fontSize:11,color:"#94a3b8",marginBottom:8,fontWeight:700}}>مثال على الراتب</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:12}}>
              {[0,10000,20000,40000].map(net=>(
                <div key={net} style={{textAlign:"right"}}>
                  <div style={{color:"#64748b",fontSize:10}}>صافي {net.toLocaleString()}</div>
                  <div style={{color:"#c8a03c",fontWeight:800}}>{calcTotal(net,tiersForm.tiers,tiersForm.fixedSalary).toLocaleString()} ريال</div>
                  <div style={{color:"#475569",fontSize:10}}>{getTierLabel(net,tiersForm.tiers)}</div>
                </div>
              ))}
            </div>
          </div>

          <SaveBtn onClick={()=>{ setOpsConfig(p=>({...p,...tiersForm})); setEditTiers(false); }}/>
        </Modal>
      )}

      {/* Edit Month Modal */}
      {editMonth&&(
        <Modal title={`تعديل صافي دخل — ${MONTHS.find(m=>m.key===editMonth)?.label}`} onClose={()=>setEditMonth(null)}>
          <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"12px 14px",marginBottom:14,fontSize:12,color:"#64748b",lineHeight:1.8}}>
            صافي الدخل يُحسب تلقائياً من التشغيل الشهري. أدخل قيمة يدوية إذا أردت تجاوز الحساب التلقائي.
          </div>
          <div style={{marginBottom:13}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>صافي الدخل (ريال) — اترك فارغاً للاستخدام التلقائي</label>
            <input type="number" value={monthForm.netIncome||""} onChange={e=>setMonthForm(f=>({...f,netIncome:e.target.value}))}
              style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,outline:"none",direction:"ltr",textAlign:"left",boxSizing:"border-box"}}/>
          </div>
          {monthForm.netIncome&&(
            <div style={{background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:9,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8,fontSize:12}}>
              <span style={{color:"#64748b"}}>العمولة: <b style={{color:"#22c55e"}}>{calcBonus(Number(monthForm.netIncome),cfg.tiers).toLocaleString()} ريال</b></span>
              <span style={{color:"#64748b"}}>الراتب الإجمالي: <b style={{color:"#c8a03c"}}>{calcTotal(Number(monthForm.netIncome),cfg.tiers,cfg.fixedSalary).toLocaleString()} ريال</b></span>
              <span style={{color:"#a78bfa"}}>{getTierLabel(Number(monthForm.netIncome),cfg.tiers)}</span>
            </div>
          )}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>ملاحظات</label>
            <input value={monthForm.note||""} onChange={e=>setMonthForm(f=>({...f,note:e.target.value}))}
              style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,outline:"none",fontFamily:"'Tajawal',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            {(cfg.monthlyData?.[editMonth])&&(
              <button onClick={()=>{ setOpsConfig(p=>{ const md={...p.monthlyData}; delete md[editMonth]; return {...p,monthlyData:md}; }); setEditMonth(null); }}
                style={{padding:"10px 16px",borderRadius:10,background:"rgba(239,68,68,0.09)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                إزالة التجاوز
              </button>
            )}
            <div style={{flex:1}}>
              <SaveBtn onClick={()=>{
                if(monthForm.netIncome!==""){
                  setOpsConfig(p=>({...p,monthlyData:{...p.monthlyData,[editMonth]:{netIncome:Number(monthForm.netIncome),note:monthForm.note||""}}}));
                }
                setEditMonth(null);
              }}/>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SignaturePage — Customer view at /sign?d=…
// Renders an invoice summary + signature pad. On submit, generates a
// signed PDF and lets the customer download/share back via WhatsApp.
// ══════════════════════════════════════════════════════════════════
